/* eslint-disable arrow-body-style, max-len, no-unused-vars, padded-blocks, prefer-destructuring */
/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';
import moment from 'moment';

export const namespaced = true;

function initPlan() {
  return {
    id: null,
    planType: null,
    sites: [],
    siteGraphics: [],
    siteObjects: [],
    basic: {
      planName: null,
      author: null,
      planMonthYear: moment().startOf('month').toDate(),
    },
    propertyDescription: {
      coordinatesLatLong: '',
      coordinatesUTM: '',
      zoning: null,
      fireDistricts: null,
      nativeSeedZones: null,
    },
    introduction: {
      background: '',
      goals: '',
    },
    propertyOverview: {
      topography: '',
      soilsAndGeology: '',
      waterResources: '',
      waterRights: '',
      irrigation: '',
      propertyAccess: '',
    },
    resourceAnalysis: {
      landUseDescription: '',
      forestVegetation: '',
      crops: '',
      pastureAndLivestock: '',
      associatedAgriculturalLands: '',
      fishAndWildlife: '',
      integratedPestManagement: '',
      wildfirePreparedness: '',
      culturalResources: '',
      aestheticsAndRecreation: '',
      marketingEcosystemServices: '',
      regulatoryCompliance: '',
      nutrientAndSoilManagement: '',
    },
    managementRecommendations: {
      practices: [],
    },
    unitDetails: [],
  };
}
function defaultUnitDetailsText() {
  return `
  <div><strong><u>Unit [Name/ID]</u></strong></div>
  <div><strong>Acres:</strong> ##</div>
  <div><strong>Age:</strong> ##</div>
  <div><strong>Douglas-fir (50-yr) site index:</strong> ###</div>
  <div><strong>Slope:</strong> ## - ##, [direction]-facing</div>
  <div><strong>Elevation:</strong> ### ###</div>
  <hr/>
  <div>
  <strong>Description:</strong> [Include number of transects, stand history, primary species, open or closed canopy, horizontal structure (wild, even space, even spacing with openings, variable density), vertical structure (single canopy, two aged, three aged)]
  </div>
  <ul>
  <li>Stand has about ### TPA</li>
  <li>Spacing between trees: ### feet</li>
  <li>Basal area per acre: ### square feet</li>
  <li>Conifers:</li>
    <ul>
      <li>[Species]</li>
      <li>### conifers per acre</li>
      <li>Average DBH: ##</li>
      <li>Basal area per acre: ## square feet</li>
    </ul>
  <li>Hardwoods:</li>
    <ul>
      <li>[Species]</li>
      <li>### hardwoods per acre</li>
      <li>Average DBH: ##</li>
      <li>Basal area per acre: ## square feet</li>
    </ul>
  <li>Shrubs and ground cover:</li>
    <ul>
      <li>The stand has (light, moderate, abundant) native shrubs and ground cover (about ##% of soil surface)</li>
      <li>[Species and abundance levels]</li>
    </ul>
  <li>Invasive species:</li>
    <ul>
      <li>
        [Species and abundance levels]
      </li>
    </ul>
  </ul>
  <div>
    Management Recommendations:
  </div>
  <ul style="margin-bottom: 7px">
    <li>[Insert specific management recommendations: Thinning options and specifications (if necessary; use diameter thresholds as guide for contractors); release specifications (if necessary); any underplanting for diversity; insect and/or disease management; mitigating wind throw risk; afforestation opportunities]</li>
    <li>Control invasive species using Integrated Pest Management (IPM) techniques described in the next section of this plan.</li>
    <li>[Insert snag information and management]</li>
  </ul>
  </br></br>`;
}

export const state = {
  managementPlan: initPlan(),
  targetSeasons: [],
  practiceTypes: [],
  submitError: null,
  submitSuccess: false,
  managementPlans: {},
  initialUnitDetails: defaultUnitDetailsText(),
};

async function getAssessment(id) {
  return api.get(`/siteassessment/${id}`)
    .then((response) => response.data)
    .catch(() => {
      console.warn('No assessment found');
      return null;
    });
}
async function getVisit(id) {
  return api.get(`/sitevisit/${id}`)
    .then((response) => response.data)
    .catch(() => {
      console.warn('No visit found');
      return null;
    });
}

function getForestPropertyAccessText() {
  return `The property is accessed from <strong><em>[describe access location]</em></strong>. Roads and trails on the property are shown on the attached map. <strong><em>[Provide general description of roads. For each road segment, describe surface type, drainage type, length, stream crossings, gates, and fire use. Describe any areas that need repair.]</em></strong><br/><br/>
It is a good practice to regularly check roads and trails for evidence of water damage and erosion. If a commercial thinning operation is planned, there will need to be a plan for where to establish a landing and where to allow equipment to access and move around the property. <strong><em>[Provide options for a landing location]</em></strong>. If an operation takes place in the winter months, roads will need to be rocked to avoid rutting and erosion. Further assistance with this issue can be provided by Tualatin SWCD, an ODF stewardship forester, or a consulting forester. <br/><br/>
Currently, there do not seem to be signs of trespass, theft, or vandalism onsite <strong><em>[update based on inventory observations]</em></strong>. No Trespassing signs on the boundaries can be beneficial to keeping unwelcome guests from entering the property. Boundaries and corners are marked <strong><em>[describe boundary markings]</em></strong>.`;
}
function getAgriculturealPropertyAccessText() {
  return `The property is accessed from <strong><em>[describe access location]</em></strong>. Roads, gates, and trails on the property are shown on the attached map. <strong><em>[Provide general description of roads]</em></strong>. It is a good practice to regularly check roads for evidence of water damage and erosion. <br/>
Currently, there do not seem to be signs of trespass, theft, or vandalism onsite <strong><em>[update based on inventory observations]</em></strong>. No Trespassing signs on the boundaries can be beneficial to keeping unwelcome guests from entering the property. Boundaries and corners are marked <strong><em>[describe boundary markings]</em></strong>. Access points and boundaries should be well signed to discourage trespassing and dumping. Gates at access points may be necessary. <br/>
Other farm infrastructure includes <strong><em>[describe]</strong></em>`;
}
function getForestLandUse() {
  return 'The <strong><em>[landowner name]</em></strong> property is comprised of <strong><em>[describe land uses]</em></strong>. About <strong><em>[###]</em></strong> acres of the property are forested and will be described in <strong><em>[##]</em></strong> units below. At the time of Euro-American settlement in the Tualatin basin, this property was likely <strong><em>[insert description of 1850 vegetation]</em></strong>. Currently, <strong><em>[describe overview of forest vegetation composition]</em></strong>. The vegetation management recommendations in this section will consider historical conditions, current conditions, and potential future influences like climate and forest product markets.';
}
function getAgriculturalLandUse() {
  return 'The <strong><em>[landowner name]</em></strong> property is comprised of <strong><em>[describe land uses]</em></strong>. Each of these land uses are further described in the appropriate section.';
}
function getForestVegetationText() {
  return 'Each unit summary in this section is based on measurements and observations collected in the field by Tualatin SWCD staff. The number of trees per acre (TPA), average diameter at breast height (DBH), and proportion of hardwood to conifer trees is an average of measurements collected across several plots or transects. Tree spacing combined with average tree diameter provides a stand density estimate (i.e. whether the stand is too dense, just right, or understocked). DBH is used to estimate basal area, which is the cross-sectional view of the trees at breast height, indicating how much harvestable wood per square foot is present. Relative densities of groundcover species are listed. Snags were identified and measured; a snag is a forestry term for a standing dead tree. Snags are beneficial to wildlife as nesting and foraging habitat. Any invasive weeds are identified and highlighted with specific management recommendations in the Integrated Pest Management section.';
}
function getForestFishAndWildlifeText() {
  return `<p><strong><em>[Describe species observed on the property. Reference landowner wildlife management goals.]</em></strong></p>
<p>Each species has unique cover, water, food, and space requirements, though forests can often meet these needs for an abundance of species. This forest already has many great habitat elements: </p>
<ul>
<li>
Cover: <strong><em>[Update as needed]</em></strong> A healthy forest canopy across the property provides cover. Some species prefer shrubs while others prefer trees; the structural and species diversity across the property provides for both. There are multiple snags with cavities that likely provide nesting space for certain species.
</li>
<li>
Water: <strong><em>[Update as needed]</em></strong> Water is provided by multiple, perennial streams on the property. The vegetation along the streams allows some cover as wildlife move through these areas, as well as shade to keep the water temperature cool for fish and amphibians. Water quality appears to be high as roads and slopes are managed to reduce erosion.
</li>
<li>
Food: <strong><em>[Update as needed; list species that provide food resources on the property]</em></strong>. Understory shrubs are especially important food resources and allow for nesting cover in the spring and early summer.
</li>
<li>
Space: <strong><em>[Update as needed]</em></strong> The size of this property allows for several species of wildlife to occur and find much of what they need all on this property alone.
</li>
</ul>
There are areas that the landowners could enhance for fish and wildlife:
<ul>
<li>
Thin: <strong><em>[Update as needed]</em></strong> In the Forest Vegetation section of this plan, several units have some level of forest thinning recommended. This thinning will help to grow larger trees faster. Older, larger trees are great for wildlife. Maintain some species diversity in the unit where possible. Thinning of some of the shrub layer is okay for tree growth, but it is important not to decimate the shrub layer by leaving many of them intact. Consider cutting back hazel so that it will re-sprout at ground level to provide forage for deer and elk.
</li>
<li>
Plant: <strong><em>[Update as needed]</em></strong> After thinning, consider planting western redcedar, western hemlock, and/or grand fir in canopy openings. Also consider planting shrub species on forest edges and in canopy openings such as <strong><em>[insert recommended shrub species]</em></strong>. Tree and shrub diversity often enhance wildlife diversity.
</li>
<li>
Retain existing snags: Retain dying trees as future snags, especially those large enough to provide significant wildlife habitat (greater than 12 inches in diameter). Do not remove existing snags where possible. <br/>
</li>
</ul>
<strong><em>[Insert threatened or endangered species on or near site and management recommendations.]</em></strong>`;
}
function getAgriculturalFishAndWildlifeText() {
  return `<p><strong><em>[Describe species observed on the property. Reference landowner wildlife management goals.]</em></strong></p>
<p>Farms provide important habitat for fish and wildlife. Each species has unique cover, water, food, and space requirements. This farm already has many great habitat elements:</p>
<ul>
<li>
Cover: <strong><em>[Update as needed]</em></strong> Areas of forest and vegetation provide cover for a variety of wildlife species. There are multiple snags with cavities that likely provide nesting space for certain species.
</li>
<li>
Water: <strong><em>[Update as needed]</em></strong> Water is provided by multiple, perennial streams on the property. The vegetation along the streams allows some cover as wildlife move through these areas, as well as shade to keep the water temperature cool for fish and amphibians. Water quality appears to be high as roads and slopes are managed to reduce erosion.
</li>
<li>
Food: <strong><em>[Update as needed; list species that provide food resources on the property]</em></strong>. Understory shrubs are especially important food resources and allow for nesting cover in the spring and early summer.
</li>
<li>
Space: <strong><em>[Update as needed]</em></strong> The size of this property allows for several species of wildlife to occur and find much of what they need all on this property alone.
</li>
</ul>
<p>There are areas that the landowners could enhance for fish and wildlife: </p>
<ul>
<li>
Plant: <strong><em>[Update as needed]</em></strong> Consider planting native species on forest and field edges. Tree and shrub diversity often enhance wildlife diversity.
</li>
<li>
Retain existing snags: Retain dying trees as future snags, especially those large enough to provide significant wildlife habitat (greater than 12 inches in diameter). Do not remove existing snags where possible.
</li>
</ul>
<p><strong><em>[Insert threatened or endangered species on or near site and management recommendations.]</em></strong></p>`;
}
function getForestPestManagementText() {
  return `<p>There are some areas where invasive plant species are threatening forest health to a small extent <strong><em>[update based on inventory observations]</em></strong>. The following Integrated Pest Management (IPM) recommendations are based on strategies implemented by partners of the 4-County Cooperative Weed Management Area (CWMA):</p>
<p><strong><em>[For each weed, provide common name, units where weed was observed, and IPM strategy.]</em></strong></p>
<p>Always monitor for invasive weeds as they have a chance to arrive on the property at any time. Familiarize yourself with common invasive weeds in the Tualatin basin and pay attention to plants you have not seen before and/or plants that appear to be slowly dominating an area. Many of these species can be ecosystem changers and will often spread successfully across a property without human intervention.</p>
<p><strong><em>[Describe any insect or disease issues noted on the property. For each concern, describe the location, signs and symptoms, and any necessary management actions.]</em></strong></p>`;
}
function getAgriculturalPestManagementText() {
  return `
<p>Integrated Pest Management (IPM) is a decision-making strategy and set of actions that addresses pest control (including weeds, insects, and disease) in an environmentally and economically sound manner to meet pest management goals. This includes preventing invasions, regular monitoring, establishing a threshold for pest tolerance, treating pests, and evaluating treatment effectiveness. </p>
<p>Invasive plants and pests on the property include <strong><em>[describe]</strong></em></p>
<p>Current strategies to manage weeds and pests include <strong><em>[describe]</strong></em></p>
<p>Integrated Pest Management strategy examples include:</p>
<ul>
<li>
Crop rotation
</li>
<li>
Soil and crop nutrient management
</li>
<li>
Cover crop, green manures, residue
</li>
<li>
Diversified planting
</li>
<li>
Removal of weed seeds and pest habitat
</li>
<li>
Species selection and locations
</li>
<li>
Timing of planting
</li>
<li>
Irrigation water management
</li>
<li>
Mechanical approaches (hoeing, pruning, picking, vacuuming)
</li>
<li>
Introduction of pest predators/beneficials
</li>
<li>
Develop habitat for natural enemies
</li>
<li>
Construct habitat for predators (raptor perches, owl or bat boxes)
</li>
<li>
Cultivation/tillage
</li>
<li>
Mowing or livestock grazing
</li>
<li>
Mulching
</li>
<li>
Burning
</li>
<li>
Herbicide/insecticide use
</li>
</ul>
<p>The following is a table of IPM strategies to help control specific invasive species known to occur on the property. The recommendations are based on strategies implemented by the partners of the 4-County Cooperative Weed Management Area (CWMA) and Oregon Department of Agriculture (ODA). </p>`;
}
function getForestWildfirePreparednessText() {
  return `
  <p>The risk of wildfire on this property is relatively low. Most of the site is accessible and the fuel build up is not excessive.</p>
  <ul>
  <li>Recommended thinning will decrease risk further by lowering the fuels as long as slash is treated properly and ladder fuels are not created with cut trees leaning against living trees.</li>
  <li>Most of the downed trees should be removed as firewood or stacked into brush piles that are not directly below overhanging branches. Leaving some downed wood for habitat is okay.</li>
  <li>Some pruning could be helpful in dense stands.</li>
  </ul>
  <p>The trail system could be updated with rocked roads at about 12-foot width to accommodate emergency vehicles. In the winter, there are some mud issues that would prevent a truck from accessing the entire site, but in the summer when wildfire is more likely, a brush rig could get around the property pretty well if roads are built and maintained on key trails. Near the home there are plenty of places to bring emergency vehicles and places to turn around.</p>
  <p>Near the home, keep a fire safe space where 30-50 feet around buildings is kept clear of trees and shrubs that could carry a fire to the structures.</p>
  <p>Consider creating a separate wildfire preparedness plan, including who to call when a wildfire occurs (emergency response and family/neighbors/etc.), evacuation routes that vary based on where the fire might occur, and roads to use. Include a map that identifies structures, fire breaks, water sources, power lines, cell towers, roads, and turn around areas on your property and have an extra copy ready for firefighters.</p>
  `;
}
function getForestCulturalResourcesText() {
  return `
  <p>There are no known significant historic or cultural sites on the property. Oregon Department of Forestry has checked their data from the State Historic Preservation Officer (SHPO) and have no record of concern in this section. If the landowners ever wish to pursue funding through the Natural Resources Conservation Service (NRCS) to make forest improvements, a consultation between the NRCS State Archaeologist and SHPO will occur to review if there are any known sites on the property.</p>
  `;
}
function getAgricultureCulturalResourcesText() {
  return `
  <p>There are no known significant historic or cultural sites on the property. If the landowners ever wish to pursue funding through the Natural Resources Conservation Service (NRCS) to make farm improvements, a consultation between the NRCS State Archaeologist and SHPO will occur to review if there are any known sites on the property.</p>
  `;
}
function getForestAestheticsAndRecreationText() {
  return `
  <p>The forest is a great place for recreation. There are some trails throughout the site that allow access for walks and hikes, though they could be expanded to increase access year around and within each unit. The aesthetic appeal of the forest might be improved with some forest restoration that thins smaller shrubs and hardwood trees like alder, maple, and cherry, allowing the conifers to grow larger. Planting a diversity of shade-tolerant conifers could also improve the diversity of color and texture in the forest throughout the year. Planting more flowering shrubs in sunny units will allow for a variety of colors in spring and early summer, as well as wildlife watching throughout the growing season.</p>
  `;
}
function getForestMarketingEcosystemServicesText() {
  return `
  <p>Planting Douglas-fir and other fast-growing conifers on the property will sequester carbon at a high rate. Timely thinning will maintain and/or increase the rate of sequestration. If possible, woody material harvested from the stand should be converted to forest products that continue the storage of accumulated carbon.</p>
  <p>There is no immediate objective to market ecosystem services. Organizations like the Pinchot Institute will make carbon payments to landowners who are managing forests to an older age, which can supplement forest income on the property. A carbon inventory would need to take place to see if the property is a good fit for the program. Properties selected for carbon sequestration programs tend to be over 100 acres.</p>
  `;
}
function getForestRegulatoryComplianceText() {
  return `
  <p>All forest management activities recommended or described in this plan comply with the Oregon Forest Practices Act. Care should be taken not to impact streams and seasonal drainages on the property. At this time, no significant management is planned near streams.</p>
  <p>A Forest Practices notification will need to be submitted to ODF at least 15 days before most activities take place. Planned activities that will need to be documented in an ODF notification include (though are not limited to):</p>
  <ul>
  <li>Applying herbicide</li>
  <li>Pre-commercial and commercial thinning</li>
  <li>Regeneration harvests (clear cuts)</li>
  <li>Repairing roads</li>
  <li>Using power driven machinery on the property (to haul logs, harvest trees, build road, etc.)</li>
  </ul>
  <p>Notifications are only valid for practices implemented in the current calendar year. Landowners should plan to communicate with the ODF Stewardship Forester to make the notification process easier when or if they need to file a notification. Landowners should be sure to comply with all local, state and federal laws that regulate forest land.</p>
  `;
}
function getAgriculturalRegulatoryComplianceText() {
  return `
  <p>Landowners must comply with several state, federal, and local laws in managing their property.</p>
  `;
}
function getAgriculturalNutrientAndSoilText() {
  return `<p>Nutrient management addresses management of manure and wastewater, fertilizer, plant nutrient uptake, and soil fertility on the farm. This includes the production and storage of potential nutrients on the farm, as well as the application of nutrients, runoff and erosion, and soil and water quality issues. Soil tests should help inform nutrient application rates and should be updated every 5 years, per field.</p>
<p>Nutrients added to the property consist of <strong><em>[describe]</strong></em></p>
<p>Current soil management and erosion conditions on the property consist of <strong><em>[describe]</strong></em></p>
Best practices for building and maintaining soil health and avoiding erosion include:
<ul>
<li>Adding organic material from diverse sources</li>
<li>Minimizing loss of native soil</li>
<li>Keeping the soil covered with vegetation, cover crops, or crop residue</li>
<li>Minimizing tillage</li>
<li>Limit compacting activities</li>
<li>Manage soil fertility based on crop and soil needs</li>
<li>Avoid steep slopes</li>
<li>Irrigate efficiently and avoid runoff</li>
</ul>`;
}
export const actions = {
  async updateAssessmentTextInIntroduction({ commit }, siteIds) {
    const assessmentCalls = siteIds.map((id) => getAssessment(id));
    const assessments = await Promise.all(assessmentCalls);
    const visitCalls = siteIds.map((id) => getVisit(id));
    const visits = await Promise.all(visitCalls);
    commit('UPDATE_TEXT_DATA', { assessments: assessments.filter((a) => a !== null), visits: visits.filter((v) => v !== null) });
  },
  updateUnitDetailsOnManagementPlan({ commit }, unitDetails) {
    commit('SET_UNIT_DETAILS', unitDetails);
  },
  async postManagementPlan({ commit }, managementPlan) {
    const managementPlanSiteModels = managementPlan.sites.map((site) => {
      const managementPlanSiteModel = {
        globalId: site.graphic.attributes.globalid,
        insightlyId: site.graphic.attributes.insightlyid,
        siteName: site.graphic.attributes.sitename,
      };
      return managementPlanSiteModel;
    });

    let managementPlanModel = initPlan();
    managementPlanModel.planType = managementPlan.planType;
    managementPlanModel.sites = managementPlanSiteModels;

    managementPlanModel = await api.post('/managementplan', managementPlanModel).then(async (response) => {
      this.successMessage = 'The management plan was successfully created.';
      commit('SET_SUBMIT_SUCCESS', true);
      return response.data;
    }).catch((error) => {
      console.error(error);
      commit('SET_SUBMIT_SUCCESS', false);
      commit('SET_SUBMIT_ERROR', error);
    });

    return commit('SET_MANAGEMENTPLANID', managementPlanModel.id);
  },
  initManagementPlan({ commit }) {
    commit('SET_MANAGEMENTPLAN', initPlan());
  },
  async updateManagementPlan({ commit }, managementPlan) {
    await api.put('/managementplan', managementPlan)
      .then(() => {
        commit('SET_SUBMIT_SUCCESS', true);
        commit('SET_MANAGEMENTPLAN', managementPlan);
      })
      .catch((error) => {
        console.error(error);
        commit('SET_SUBMIT_SUCCESS', false);
        commit('SET_SUBMIT_ERROR', error);
      });
  },
  setSubmitSuccess({ commit }, value) {
    return commit('SET_SUBMIT_SUCCESS', value);
  },
  getBasicsDateDefault() {
    return new Date();
  },
  setManagementPlan({ commit }, managementPlan) {
    return commit('SET_MANAGEMENTPLAN', managementPlan);
  },
  async getManagementPlansAsync({ commit }, siteId) {
    await api.get(`/managementplan/site/${siteId}`).then((response) => {
      const result = commit('SET_MANAGEMENTPLANS', response.data);
      return result;
    }).catch((exception) => {
      console.warn(exception);
    });
  },
  deleteManagementPlan({ commit }, params) {
    api.delete(`/managementplan/${params.managementPlanId}`).then(
      () => {
        api.get(`/managementplan/site/${params.siteId}`).then(
          (response) => commit('SET_MANAGEMENTPLANS', response.data),
        ).catch((exception) => {
          console.warn(exception);
        });
      },
    ).catch((exception) => {
      console.warn(exception);
    });
  },
  addPracticeToCurrentPlan({ commit }, practice) {
    commit('ADD_PRACTICE', practice);
  },
  updatePracticeToCurrentPlan({ commit }, practice) {
    commit('UPDATE_PRACTICE', practice);
  },
  deletePracticeToCurrentPlan({ commit }, practice) {
    commit('DELETE_PRACTICE', practice);
  },
  fetchTargetSeasons({ commit }) {
    return api.get('/managementPlan/targetSeasons')
      .then((response) => commit('SET_TARGET_SEASONS', response.data))
      .catch(() => {
        console.warn('No target seasons found');
        return null;
      });
  },
  fetchPracticeTypes({ commit }) {
    return api.get('/managementPlan/practiceTypes')
      .then((response) => commit('SET_PRACTICE_TYPES', response.data))
      .catch(() => {
        console.warn('No practice types found');
        return null;
      });
  },
  // fetchManagementPlan({ commit, state }, id) {
  //   return api.get(`/managementPlan/${id}`)
  //     .then((response) => commit('SET_MANAGEMENTPLAN', response.data))
  //     .catch(() => {
  //       return commit('SET_MANAGEMENTPLAN', initPlan());
  //     });
  // },
  fetchManagementPlan({ commit, state }, id) {
    const promise = api.get(
      `/managementPlan/${id}`,
    ).then(
      (response) => {
        if (response.data.managementRecommendations.practices && response.data.managementRecommendations.practices != null) {
          response.data.managementRecommendations.practices.forEach((e) => {
            e.surrogateId = e.id;
          });
        }
        commit('SET_MANAGEMENTPLAN', response.data);
      },
    ).catch(
      () => {
        console.error('No management plan found');
        return commit('SET_MANAGEMENTPLAN', initPlan());
      },
    );
    return promise;
  },
};

export const mutations = {
  // ADD_PRACTICE(state, newPractice) {
  //   if (!state.managementPlan.managementRecommendations.practices) {
  //     state.managementPlan.managementRecommendations.practices = [newPractice];
  //   } else {
  //     state.managementPlan.managementRecommendations.practices.push(newPractice);
  //   }
  // },
  // UPDATE_PRACTICE(state, params) {
  //   const recommendations = state.managementPlan.managementRecommendations;
  //   if (recommendations.practices && recommendations.practices.length > params.practiceIndex) {
  //     recommendations.practices.splice(params.practiceIndex, 1, params.practice);
  //   }
  // },
  // DELETE_PRACTICE(state, indexToDelete) {
  //   const recommendations = state.managementPlan.managementRecommendations;
  //   if (recommendations.practices && recommendations.practices.length > indexToDelete) {
  //     recommendations.practices.splice(indexToDelete, 1);
  //   }
  // },
  ADD_PRACTICE(state, practice) {
    if (!state.managementPlan.managementRecommendations.practices) {
      state.managementPlan.managementRecommendations.practices = [practice];
    } else {
      state.managementPlan.managementRecommendations.practices.push(practice);
    }
  },
  UPDATE_PRACTICE(state, practice) {
    const recommendations = state.managementPlan.managementRecommendations;
    const index = recommendations.practices.findIndex((e) => e.surrogateId === practice.surrogateId);
    recommendations.practices.splice(index, 1, practice);
  },
  DELETE_PRACTICE(state, practice) {
    const recommendations = state.managementPlan.managementRecommendations;
    const index = recommendations.practices.findIndex((e) => e.surrogateId === practice.surrogateId);
    recommendations.practices.splice(index, 1);
  },
  SET_UNIT_DETAILS(state, unitDetails) {
    state.managementPlan.unitDetails = unitDetails;
  },
  SET_MANAGEMENTPLAN(state, managementPlan) {
    state.managementPlan = managementPlan;
  },
  SET_MANAGEMENTPLANID(state, id) {
    state.managementPlan.id = id;
  },
  SET_MANAGEMENTPLANS(state, managementPlans) {
    state.managementPlans = managementPlans;
  },
  UPDATE_TEXT_DATA(state, assessmentsAndVisits) {
    const { assessments, visits } = assessmentsAndVisits;
    if (state.managementPlan.introduction.background === ''
      && state.managementPlan.introduction.goals === '' && assessments.length > 0) {
      state.managementPlan.introduction.background = `<strong>Landowner Information & Ownership:</strong> ${assessments.map((a) => a.background.landownerInformation).join('<br/>')}<br/><br/>
<strong>Historical Use:</strong><br/> ${assessments.map((a) => a.background.historicalUse).join('<br/><br/>')}<br/><br/>
<strong>Current Use:</strong><br/> ${assessments.map((a) => a.background.currentUse).join('<br/><br/>')}<br/><br/>
<strong>General Conditions:</strong><br/> ${assessments.map((a) => a.background.generalConditions).join('<br/><br/>')}<br/><br/>
<strong>Watershed Context:</strong><br/> ${assessments.map((a) => a.background.watershedContext).join('<br/><br/>')}`;
      state.managementPlan.introduction.goals = `<strong>Landowner Goals:</strong><br/> ${assessments.map((a) => a.goals.landownerGoals).join('<br/><br/>')}<br/><br/>
<strong>Forest Management Goals:</strong><br/> ${assessments.map((a) => a.goals.forestManagementGoals).join('<br/><br/>')}<br/><br/>
<strong>Agricultural Management Goals:</strong><br/> ${assessments.map((a) => a.goals.agriculturalManagementGoals).join('<br/><br/>')}`;
      state.managementPlan.propertyOverview.topography = assessments.map((a) => a.propertyOverview.topography).join('<br/><br/>');
      state.managementPlan.propertyOverview.soilsAndGeology = `${assessments.map((a) => a.propertyOverview.soilsAndGeology).join('<br/><br/>')}<br/>The map below shows the different soils on the property. Soils in the area were mapped in the past by the Natural Resources Conservation Service (NRCS). The soils are listed in the table below and are mapped in the <strong><em>[Landowner]</em></strong> Soils Map image.`;
      state.managementPlan.propertyOverview.waterResources = assessments.map((a) => a.propertyOverview.waterResources).join('<br/><br/>');
      if (state.managementPlan.planType.includes('Forest')) {
        state.managementPlan.propertyOverview.propertyAccess = getForestPropertyAccessText();
        state.managementPlan.resourceAnalysis.landUseDescription = getForestLandUse();
        state.managementPlan.resourceAnalysis.forestVegetation = getForestVegetationText();
        state.managementPlan.resourceAnalysis.fishAndWildlife = getForestFishAndWildlifeText();
        state.managementPlan.resourceAnalysis
          .integratedPestManagement = getForestPestManagementText();

        state.managementPlan.resourceAnalysis
          .wildfirePreparedness = getForestWildfirePreparednessText();
        state.managementPlan.resourceAnalysis.culturalResources = getForestCulturalResourcesText();
        state.managementPlan
          .resourceAnalysis.aestheticsAndRecreation = getForestAestheticsAndRecreationText();
        state.managementPlan.resourceAnalysis
          .marketingEcosystemServices = getForestMarketingEcosystemServicesText();
        state.managementPlan.resourceAnalysis
          .regulatoryCompliance = getForestRegulatoryComplianceText();

      } else {
        state.managementPlan.propertyOverview.waterRights = assessments.map((a) => a.propertyOverview.waterRights).join('<br/><br/>');
        if (visits && visits.length > 0) {
          state.managementPlan.propertyOverview.irrigation = visits.map((v) => v.agriculturalDetails.irrigationMethods.join(', ')).join('<br/>');
          state.managementPlan.resourceAnalysis.crops = `Current crops on the farm include: <br/>${visits.map((v) => v.agriculturalDetails.cropsBeingGrown.join(', ')).join('<br/>')}`;
        }
        state.managementPlan.propertyOverview.propertyAccess = getAgriculturealPropertyAccessText();
        state.managementPlan.resourceAnalysis.landUseDescription = getAgriculturalLandUse();
        state.managementPlan.resourceAnalysis.pastureAndLivestock = 'Current pastures on the property include <strong><em>[describe]</strong></em> <br/>Best Management Practices for pasture management consist of <strong><em>[describe]</strong></em> <br/>Current livestock on the property include <strong><em>[describe]</strong></em>';
        state.managementPlan.resourceAnalysis.associatedAgriculturalLands = 'Non-farmed areas of the property such as natural areas, buffers, and fallow fields include <strong><em>[describe]</strong></em>';
        state.managementPlan.resourceAnalysis
          .fishAndWildlife = getAgriculturalFishAndWildlifeText();
        state.managementPlan.resourceAnalysis
          .integratedPestManagement = getAgriculturalPestManagementText();

        state.managementPlan.resourceAnalysis
          .culturalResources = getAgricultureCulturalResourcesText();
        state.managementPlan.resourceAnalysis
          .regulatoryCompliance = getAgriculturalRegulatoryComplianceText();

        state.managementPlan.resourceAnalysis
          .nutrientAndSoilManagement = getAgriculturalNutrientAndSoilText();
      }
    }
  },
  SET_TARGET_SEASONS(state, targetSeasons) {
    state.targetSeasons = targetSeasons;
  },
  SET_PRACTICE_TYPES(state, practiceTypes) {
    state.practiceTypes = practiceTypes;
  },
  SET_SUBMIT_ERROR(state, error) {
    state.submitError = error;
  },
  SET_SUBMIT_SUCCESS(state, value) {
    state.submitSuccess = value;
  },
};
function getPracticeType(id, practiceTypes) {
  return practiceTypes.filter((x) => x.id === id)[0];
}
function getTargetSeason(id, targetSeasons) {
  return targetSeasons.filter((x) => x.id === id)[0];
}
export const getters = {

  // I see the problem...no wonder updates aren't working.
  // We can't use item index to update items because the order of the underlying dataset
  // is not the same as the sort order of the presentation dataset

  // currentVisiblePractices: (state) => state.managementPlan.managementRecommendations.practices
  //   .map((practice) => ({
  //     id: practice.id,
  //     practiceType: getPracticeType(practice.practiceTypeId, state.practiceTypes),
  //     targetSeason: getTargetSeason(practice.targetSeasonId, state.targetSeasons),
  //     targetYear: new Date(practice.targetYear),
  //     unitsFields: practice.unitsFields,
  //     acres: practice.acres,
  //     notes: practice.notes,
  //     practiceTypeId: practice.practiceTypeId,
  //     targetSeasonId: practice.targetSeasonId,
  //   })).sort((a, b) => (a.targetYear.getFullYear() - b.targetYear.getFullYear())
  //     || (a.targetSeason.sortOrder - b.targetSeason.sortOrder)
  //     || (a.unitsFields - b.unitsFields)),

  // Keeping this reformatted because the original code above is unreadable

  currentVisiblePractices: (state) => {
    let currentVisiblePractices = state.managementPlan.managementRecommendations.practices.map((practice) => ({
      id: practice.id,
      surrogateId: practice.surrogateId,
      practiceType: getPracticeType(practice.practiceTypeId, state.practiceTypes),
      targetSeason: getTargetSeason(practice.targetSeasonId, state.targetSeasons),
      targetYear: new Date(practice.targetYear),
      unitsFields: practice.unitsFields,
      acres: practice.acres,
      notes: practice.notes,
      practiceTypeId: practice.practiceTypeId,
      targetSeasonId: practice.targetSeasonId,
    }));

    currentVisiblePractices = currentVisiblePractices.sort(
      (a, b) => (a.targetYear.getFullYear() - b.targetYear.getFullYear()) || (a.targetSeason.sortOrder - b.targetSeason.sortOrder) || (a.unitsFields - b.unitsFields),
    );

    return currentVisiblePractices;
  },
};
